import React, { useEffect, useRef, useState, useCallback } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { PhotoIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import useManageLandingSchedule from './useManageLandingSchedule';

Modal.setAppElement('#root');

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    maxWidth: '420px',
    width: '90%',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '12px',
    padding: '24px',
  },
};

function currentYm() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function recordFor(items, isAir) {
  return items.find((r) => r.isAir === isAir) || null;
}

function ScheduleCard({
  title,
  description,
  isAir,
  year,
  month,
  record,
  isLoading,
  onUpsert,
  onDeleteRequest,
}) {
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const clearPending = useCallback(() => {
    setPendingFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }
    setPendingFile(file);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    const ok = await onUpsert({ year, month, isAir, file: pendingFile });
    if (ok) {
      toast.success('บันทึกรูปแล้ว');
      clearPending();
    } else {
      toast.error('อัปโหลดไม่สำเร็จ');
    }
  };

  const currentUrl =
    record?.imageUrl && record.imageUrl !== 'pending_upload'
      ? record.imageUrl
      : null;
  const isPendingServer = record?.imageUrl === 'pending_upload';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-500">{description}</p>
      </div>

      <div className="mb-4">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
          รูปที่ใช้งานอยู่
        </p>
        <div className="flex min-h-[160px] items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-200 bg-slate-50">
          {isPendingServer ? (
            <span className="text-sm text-amber-600">กำลังอัปโหลดไปเซิร์ฟเวอร์…</span>
          ) : currentUrl ? (
            <img
              src={currentUrl}
              alt={title}
              className="max-h-64 w-full object-contain"
            />
          ) : (
            <span className="text-sm text-slate-400">ยังไม่มีรูปสำหรับเดือนนี้</span>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPickFile}
      />

      {!previewUrl ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={isLoading}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <PhotoIcon className="h-5 w-5" />
            เลือกรูป
          </button>
          {record?.id ? (
            <button
              type="button"
              disabled={isLoading}
              onClick={() => onDeleteRequest(record)}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <TrashIcon className="h-5 w-5" />
              ลบรูป
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4 rounded-lg border border-indigo-100 bg-indigo-50/50 p-4">
          <p className="text-sm font-medium text-indigo-900">ตัวอย่างก่อนยืนยัน</p>
          <div className="overflow-hidden rounded-lg border border-white bg-white shadow-inner">
            <img
              src={previewUrl}
              alt="ตัวอย่าง"
              className="max-h-72 w-full object-contain"
            />
          </div>
          {currentUrl ? (
            <p className="text-xs text-slate-600">
              การยืนยันจะแทนที่รูปเดิมในระบบ (เดือนเดียวกัน ประเภทเดียวกัน)
            </p>
          ) : (
            <p className="text-xs text-slate-600">ยืนยันเพื่อบันทึกรูปใหม่</p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={isLoading}
              onClick={handleConfirmUpload}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              ยืนยันอัปโหลด
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <ArrowPathIcon className="h-4 w-4" />
              เลือกรูปอื่น
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={clearPending}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              ยกเลิก
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const LandingScheduleSetting = () => {
  const [{ year, month }, setYm] = useState(currentYm);
  const { items, isLoading, refresh, upsertImage, remove } = useManageLandingSchedule();
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    refresh(year, month);
  }, [year, month, refresh]);

  const years = [];
  const y0 = new Date().getFullYear();
  for (let y = y0 - 1; y <= y0 + 2; y += 1) years.push(y);

  const handleUpsert = async (payload) => upsertImage(payload);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const ok = await remove(deleteTarget.id, year, month);
    if (ok) {
      toast.success('ลบแล้ว');
      setDeleteTarget(null);
    } else {
      toast.error('ลบไม่สำเร็จ');
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mx-auto max-w-5xl">
        <h1 className="mb-2 text-2xl font-bold text-slate-900">
          รูปตารางเรือ / แอร์ (หน้า Landing)
        </h1>
        <p className="mb-6 text-slate-600">
          อัปโหลดทีละประเภทต่อเดือน — มีตัวอย่างก่อนยืนยัน ระบบจะแทนที่รูปเดิมถ้ามีแล้ว
        </p>

        <div className="mb-8 flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">ปี</label>
            <select
              value={year}
              onChange={(e) => setYm((s) => ({ ...s, year: Number(e.target.value) }))}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">เดือน</label>
            <select
              value={month}
              onChange={(e) => setYm((s) => ({ ...s, month: Number(e.target.value) }))}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={isLoading}
            onClick={() => refresh(year, month)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            <ArrowPathIcon className="h-4 w-4" />
            โหลดใหม่
          </button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <ScheduleCard
            title="รอบแอร์"
            description="แสดงเมื่อ isAir = บนเว็บลูกค้า"
            isAir
            year={year}
            month={month}
            record={recordFor(items, true)}
            isLoading={isLoading}
            onUpsert={handleUpsert}
            onDeleteRequest={setDeleteTarget}
          />
          <ScheduleCard
            title="รอบเรือ (ไม่ใช่แอร์)"
            description="แสดงเมื่อ isAir = ลงเว็บลูกค้า"
            isAir={false}
            year={year}
            month={month}
            record={recordFor(items, false)}
            isLoading={isLoading}
            onUpsert={handleUpsert}
            onDeleteRequest={setDeleteTarget}
          />
        </div>
      </div>

      <Modal
        isOpen={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        style={customStyles}
        contentLabel="ยืนยันลบ"
      >
        <h2 className="mb-3 text-lg font-semibold text-slate-900">ลบรูปตาราง?</h2>
        <p className="mb-6 text-sm text-slate-600">
          ประเภท: {deleteTarget?.isAir ? 'แอร์' : 'เรือ'} — เดือน {month}/{year}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            disabled={isLoading}
            onClick={confirmDelete}
            className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            ลบ
          </button>
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            className="flex-1 rounded-lg border border-slate-300 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            ยกเลิก
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LandingScheduleSetting;
